import React, { useState } from 'react';
import './App.css';
import Selector from './components/Selector';
import LanguageSelector from './components/LanguageSelector';
import DecisionTreeViewer from './components/DecisionTreeViewer';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { AppModeProvider, useAppMode, AppMode } from './context/AppModeContext';

// Configuration panel component to select the app mode
const AppModeSelector: React.FC = () => {
  const { mode, setMode, isInIframe, useRelativeUrls } = useAppMode();
  
  return (
    <div className="fixed top-0 right-0 m-4 bg-white p-3 rounded-lg shadow-md z-50">
      <label className="block mb-2 text-sm font-medium text-gray-700">Application Mode:</label>
      <select 
        value={mode} 
        onChange={(e) => setMode(e.target.value as AppMode)}
        className="block w-full p-2 border border-gray-300 rounded-md"
      >
        <option value="POS">Point of Sales</option>
        <option value="WEBSITE">Website</option>
        <option value="IFRAME">Iframe Mode</option>
      </select>
      <div className="mt-2 text-xs text-gray-600">
        <div>In Iframe: {isInIframe ? 'Yes' : 'No'}</div>
        <div>Relative URLs: {useRelativeUrls ? 'Yes' : 'No'}</div>
        <div>Referrer: {document.referrer || 'None'}</div>
        <div>Parent Origin: {(() => {
          try {
            return window.parent !== window.self ? window.parent.location.origin : 'Same window';
          } catch (e) {
            return document.referrer ? new URL(document.referrer).origin : 'Cross-origin';
          }
        })()}</div>
        <div>Language Code: {(() => {
          try {
            if (window.parent && window.parent !== window.self) {
              const pathSegments = window.parent.location.pathname.split('/').filter(segment => segment);
              if (pathSegments.length > 0 && pathSegments[0].match(/^[a-z]{2}(-[a-z]{2})?$/i)) {
                return pathSegments[0];
              }
            }
          } catch (e) {
            if (document.referrer) {
              try {
                const referrerUrl = new URL(document.referrer);
                const pathSegments = referrerUrl.pathname.split('/').filter(segment => segment);
                if (pathSegments.length > 0 && pathSegments[0].match(/^[a-z]{2}(-[a-z]{2})?$/i)) {
                  return pathSegments[0];
                }
              } catch (e2) {
                return 'Parse error';
              }
            }
          }
          return 'None detected';
        })()}</div>
      </div>
    </div>
  );
};

function AppContent() {
  const [languageSelected, setLanguageSelected] = useState(false);
  const { t } = useLanguage();
  const { mode } = useAppMode();
  
  // Check if we should show the admin tree view
  const isAdminMode = window.location.pathname.includes('/admin') || window.location.pathname.includes('/tree');
  
  const handleLanguageSelected = () => {
    setLanguageSelected(true);
  };
  
  const handleLogoClick = () => {
    // Always allow returning to language selection when clicking the logo
    setLanguageSelected(false);
  };
  
  const handleResetToLanguageSelection = () => {
    // Reset back to language selection - this will be used by the Selector component
    setLanguageSelected(false);
  };
  
  // Only show logo in POS mode
  const showLogo = mode === 'POS';
  
  return (
    <div className="min-h-screen bg-white flex flex-col">
      {isAdminMode ? (
        <DecisionTreeViewer />
      ) : (
        <>
          {showLogo && (
            <header className="py-4 px-6 md:px-10 border-b border-gray-100">
              <div className="max-w-7xl mx-auto w-full flex justify-between items-center">
                <div className="flex items-center">
                  <img 
                    src="/logo-felco.jpg" 
                    alt="FELCO" 
                    className="h-24 rounded-md cursor-pointer"
                    onClick={handleLogoClick}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "https://via.placeholder.com/600x200/ffffff/E30613?text=FELCO";
                    }}
                  />
                </div>
              </div>
            </header>
          )}
          <main className="flex-grow flex items-center justify-center py-8 px-4">
            {!languageSelected ? (
              <LanguageSelector onLanguageSelected={handleLanguageSelected} />
            ) : (
              <Selector onResetToLanguageSelection={handleResetToLanguageSelection} />
            )}
          </main>
          <footer className="py-4 px-6 md:px-10 text-felco-red text-sm text-center border-t border-gray-100">
            <div className="max-w-7xl mx-auto">
              {t('app.footer').replace('{year}', new Date().getFullYear().toString())}
            </div>
          </footer>
        </>
      )}
      
      {/* Only show mode selector in development */}
      {process.env.NODE_ENV === 'development' && <AppModeSelector />}
    </div>
  );
}

function App() {
  // You can set the initial mode here based on environment variables or URL params
  const initialMode: AppMode = 
    process.env.REACT_APP_MODE === 'POS' ? 'POS' : 'WEBSITE';
  
  return (
    <AppModeProvider initialMode={initialMode}>
      <LanguageProvider>
        <AppContent />
      </LanguageProvider>
    </AppModeProvider>
  );
}

export default App;
